import {
  AssignedUser,
  EventTypesResponse,
  SystemLoggingEventsResponse,
  SystemLoggingStats,
  TrendsResponse,
} from '../types/SystemLogging.types';
import API from './ApiBase';

type GetTrendsParams = {
  aiSystemId: string;
  months: number;
};

type GetEventsParams = {
  aiSystemId?: string;
  eventType?: string;
  severity?: 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
  severities?: string[];
  changeImpact?: string;
  userId?: string;
  action?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
};

export const getTrends = async (
  params: GetTrendsParams
): Promise<TrendsResponse> => {
  try {
    const searchParams = new URLSearchParams();
    searchParams.append('aiSystemId', params.aiSystemId);
    searchParams.append('months', params.months.toString());

    const queryString = searchParams.toString();
    const url = `/system-logging/trends?${queryString}`;

    const response = await API.get(url);

    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch trends data'
    );
  }
};

export const getEvents = async (
  params: GetEventsParams = {}
): Promise<SystemLoggingEventsResponse> => {
  try {
    const searchParams = new URLSearchParams();

    if (params.aiSystemId) searchParams.append('aiSystemId', params.aiSystemId);
    if (params.eventType) searchParams.append('eventType', params.eventType);
    if (params.severity) searchParams.append('severity', params.severity);
    if (params.severities?.length) {
      params.severities.forEach((severity) =>
        searchParams.append('severities', severity)
      );
    }
    if (params.changeImpact)
      searchParams.append('changeImpact', params.changeImpact);
    if (params.userId) searchParams.append('userId', params.userId);
    if (params.action) searchParams.append('action', params.action);
    if (params.search) searchParams.append('search', params.search);
    if (params.startDate) searchParams.append('startDate', params.startDate);
    if (params.endDate) searchParams.append('endDate', params.endDate);
    if (params.limit !== undefined)
      searchParams.append('limit', params.limit.toString());
    if (params.offset !== undefined)
      searchParams.append('offset', params.offset.toString());

    const queryString = searchParams.toString();
    const url = `/system-logging/events${queryString ? `?${queryString}` : ''}`;

    const response = await API.get(url);

    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch events data'
    );
  }
};

export const getEventsCount = async (
  aiSystemId: string
): Promise<number> => {
  try {
    const response = await getEvents({ aiSystemId, limit: 20, offset: 0 });
    return response.total || 0;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch events count'
    );
  }
};

export const getEventTypes = async (): Promise<EventTypesResponse> => {
  try {
    const response = await API.get<EventTypesResponse>(
      '/system-logging/event-types'
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch event types'
    );
  }
};

export const exportEventsCSV = async (
  params: GetEventsParams = {}
): Promise<Blob> => {
  try {
    const searchParams = new URLSearchParams();

    if (params.aiSystemId) searchParams.append('aiSystemId', params.aiSystemId);
    if (params.eventType) searchParams.append('eventType', params.eventType);
    if (params.severity) searchParams.append('severity', params.severity);
    if (params.severities?.length) {
      params.severities.forEach((severity) =>
        searchParams.append('severities', severity)
      );
    }
    if (params.changeImpact)
      searchParams.append('changeImpact', params.changeImpact);
    if (params.userId) searchParams.append('userId', params.userId);
    if (params.action) searchParams.append('action', params.action);
    if (params.search) searchParams.append('search', params.search);
    if (params.startDate) searchParams.append('startDate', params.startDate);
    if (params.endDate) searchParams.append('endDate', params.endDate);

    const queryString = searchParams.toString();
    const url = `/system-logging/events/export/csv${queryString ? `?${queryString}` : ''}`;

    const response = await API.get(url, {
      responseType: 'blob',
    });

    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to export events CSV'
    );
  }
};

export const exportEventsJSON = async (
  params: GetEventsParams = {}
): Promise<Blob> => {
  try {
    const searchParams = new URLSearchParams();

    if (params.aiSystemId) searchParams.append('aiSystemId', params.aiSystemId);
    if (params.eventType) searchParams.append('eventType', params.eventType);
    if (params.severity) searchParams.append('severity', params.severity);
    if (params.severities?.length) {
      params.severities.forEach((severity) =>
        searchParams.append('severities', severity)
      );
    }
    if (params.changeImpact)
      searchParams.append('changeImpact', params.changeImpact);
    if (params.userId) searchParams.append('userId', params.userId);
    if (params.action) searchParams.append('action', params.action);
    if (params.search) searchParams.append('search', params.search);
    if (params.startDate) searchParams.append('startDate', params.startDate);
    if (params.endDate) searchParams.append('endDate', params.endDate);

    const queryString = searchParams.toString();
    const url = `/system-logging/events/export/json${queryString ? `?${queryString}` : ''}`;

    const response = await API.get(url, {
      responseType: 'blob',
    });

    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to export events JSON'
    );
  }
};

export const exportEventsPDF = async (
  params: GetEventsParams = {}
): Promise<Blob> => {
  try {
    const searchParams = new URLSearchParams();

    if (params.aiSystemId) searchParams.append('aiSystemId', params.aiSystemId);
    if (params.eventType) searchParams.append('eventType', params.eventType);
    if (params.severity) searchParams.append('severity', params.severity);
    if (params.severities?.length) {
      params.severities.forEach((severity) =>
        searchParams.append('severities', severity)
      );
    }
    if (params.changeImpact)
      searchParams.append('changeImpact', params.changeImpact);
    if (params.userId) searchParams.append('userId', params.userId);
    if (params.action) searchParams.append('action', params.action);
    if (params.search) searchParams.append('search', params.search);
    if (params.startDate) searchParams.append('startDate', params.startDate);
    if (params.endDate) searchParams.append('endDate', params.endDate);

    const queryString = searchParams.toString();
    const url = `/system-logging/events/export/pdf${queryString ? `?${queryString}` : ''}`;

    const response = await API.get(url, {
      responseType: 'blob',
    });

    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to export events PDF'
    );
  }
};

export const getSystemLoggingStats = async (
  aiSystemId?: string
): Promise<SystemLoggingStats> => {
  try {
    const searchParams = new URLSearchParams();
    if (aiSystemId) searchParams.append('aiSystemId', aiSystemId);

    const queryString = searchParams.toString();
    const url = `/system-logging/stats${queryString ? `?${queryString}` : ''}`;

    const response = await API.get<SystemLoggingStats>(url);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch system logging stats'
    );
  }
};

export const getAssignedUsers = async (
  aiSystemId: string
): Promise<AssignedUser[]> => {
  try {
    const response = await API.get<AssignedUser[]>(
      `/system-logging/ai-systems/${aiSystemId}/assigned-users`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch assigned users'
    );
  }
};

export const updateEventChangeImpact = async (
  eventId: string,
  changeImpact: string,
  reason?: string
): Promise<void> => {
  try {
    const body: { changeImpact: string; reason?: string } = {
      changeImpact,
    };

    if (reason) {
      body.reason = reason;
    }

    await API.put(`/system-logging/events/${eventId}/change-impact`, body);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to update change impact'
    );
  }
};
